import {
  Button,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
} from '@nextui-org/react';
import {
  ExtractedSettlementCostLine,
  getReceiverCostMappings,
  normalizeReceiverCostConcept,
  OFFICIAL_COST_CATEGORIES,
  OFFICIAL_COST_CATEGORY_LABELS,
  OfficialCostCategory,
  ReceiverCostMapping,
  ResolvedSettlementCostLine,
  resolveReceiverCostLines,
  supersedeReceiverCostMapping,
} from 'common/src/api/receiver-costs';
import {
  ExporterCostCategory,
  getCustomCostCategoriesEnabled,
  getDefaultExporterCostCategory,
  getExporterCostCategories,
  getReceiverCostExporterMappings,
  setReceiverCostExporterCategories,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
} from 'common/src/api/exporter-cost-categories';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface ReceiverCostClassificationTableProps {
  costLines: ExtractedSettlementCostLine[];
  localCurrency?: string | null;
  exporterId?: number | null;
  exporterReceiverId?: number | null;
  onCostLinesChange: (lines: ExtractedSettlementCostLine[]) => void;
  onResolvedLinesChange: (lines: ResolvedSettlementCostLine[]) => void;
  onMappingsReadyChange?: (ready: boolean) => void;
  categoryFilter?: OfficialCostCategory | 'all';
  /** Only lines of this exporter category (null = without one). */
  exporterCategoryFilter?: number | null;
  mappingOnly?: boolean;
}

const ReceiverCostClassificationTable = ({
  costLines,
  localCurrency,
  exporterId,
  exporterReceiverId,
  onCostLinesChange,
  onResolvedLinesChange,
  onMappingsReadyChange,
  categoryFilter = 'all',
  exporterCategoryFilter,
  mappingOnly = false,
}: ReceiverCostClassificationTableProps) => {
  const [mappings, setMappings] = useState<ReceiverCostMapping[]>([]);
  const [pendingCategories, setPendingCategories] = useState<
    Record<string, OfficialCostCategory>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [amountDrafts, setAmountDrafts] = useState<Record<string, string>>({});
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [savingMappingId, setSavingMappingId] = useState('');
  const [exporterCategories, setExporterCategories] = useState<
    ExporterCostCategory[]
  >([]);
  const [exporterMappings, setExporterMappings] = useState<
    Record<string, number>
  >({});
  const [savingExporterConcept, setSavingExporterConcept] = useState('');
  const showExporterCategories = exporterCategories.length > 0;
  const costConceptSignature = useMemo(
    () =>
      costLines
        .map(
          (line) =>
            `${line.id}:${normalizeReceiverCostConcept(line.originalName)}`
        )
        .join('|'),
    [costLines]
  );
  const costFinancialSignature = useMemo(
    () =>
      costLines
        .map((line) => `${line.id}:${line.amount}:${line.currency}`)
        .join('|'),
    [costLines]
  );
  const hasCostLines = costLines.length > 0;
  const activeCostConcepts = useMemo(
    () =>
      new Set(
        costLines.map((line) => normalizeReceiverCostConcept(line.originalName))
      ),
    // costConceptSignature changes exactly when an id or normalized name does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [costConceptSignature]
  );

  useEffect(() => {
    setPendingCategories({});
  }, [exporterId, exporterReceiverId]);

  useEffect(() => {
    let cancelled = false;
    setExporterCategories([]);
    setExporterMappings({});
    if (!exporterId || !exporterReceiverId) return;

    (async () => {
      if (!(await getCustomCostCategoriesEnabled(exporterId))) return;
      const [categories, mappingsByConcept] = await Promise.all([
        getExporterCostCategories(exporterId, true),
        getReceiverCostExporterMappings(exporterId, exporterReceiverId),
      ]);
      if (!cancelled) {
        setExporterCategories(categories);
        setExporterMappings(mappingsByConcept);
      }
    })().catch((categoryError) => {
      console.error('Error loading exporter cost categories:', categoryError);
    });

    return () => {
      cancelled = true;
    };
  }, [exporterId, exporterReceiverId]);

  useEffect(() => {
    setPendingCategories((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([concept]) =>
          activeCostConcepts.has(concept)
        )
      );
      return Object.keys(next).length === Object.keys(current).length
        ? current
        : next;
    });
  }, [activeCostConcepts]);

  useEffect(() => {
    let cancelled = false;
    setMappings([]);
    setError('');
    onMappingsReadyChange?.(false);

    if (!hasCostLines) {
      onMappingsReadyChange?.(true);
      return;
    }
    if (!exporterId || !exporterReceiverId) return;

    setLoading(true);
    getReceiverCostMappings(exporterId, exporterReceiverId)
      .then((result) => {
        if (!cancelled) {
          setMappings(result);
          onMappingsReadyChange?.(true);
        }
      })
      .catch((mappingError) => {
        if (!cancelled) {
          console.error('Error loading receiver cost mappings:', mappingError);
          setError('No se pudieron cargar los mapeos de costos del cliente.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [exporterId, exporterReceiverId, hasCostLines, onMappingsReadyChange]);

  const resolvedLines = useMemo(
    () => resolveReceiverCostLines(costLines, mappings, pendingCategories),
    [costLines, mappings, pendingCategories]
  );

  useEffect(() => {
    onResolvedLinesChange(resolvedLines);
  }, [onResolvedLinesChange, resolvedLines]);

  useEffect(() => {
    setAmountDrafts({});
    setNameDrafts({});
  }, [costConceptSignature, costFinancialSignature]);

  const handleCategoryChange = async (
    line: ResolvedSettlementCostLine,
    category: OfficialCostCategory
  ) => {
    if (line.officialCategory === category) return;
    const normalizedName = line.normalizedName;

    if (line.mappingStatus === 'saved' && line.mappingId) {
      setSavingMappingId(line.mappingId);
      try {
        const nextMapping = await supersedeReceiverCostMapping(
          line.mappingId,
          category
        );
        setMappings((current) => [
          ...current.filter(
            (mapping) =>
              mapping.normalizedConcept !== nextMapping.normalizedConcept
          ),
          nextMapping,
        ]);
        setPendingCategories((current) => {
          const next = { ...current };
          delete next[normalizedName];
          return next;
        });
        if (!mappingOnly) {
          onCostLinesChange(
            costLines.map((costLine) =>
              normalizeReceiverCostConcept(costLine.originalName) ===
              normalizedName
                ? {
                    ...costLine,
                    reviewedOfficialCategory: category,
                    receiverCostMappingId: nextMapping.id,
                  }
                : costLine
            )
          );
        }
        toast.success(
          mappingOnly
            ? `Mapeo actualizado a ${OFFICIAL_COST_CATEGORY_LABELS[category]} para futuras liquidaciones.`
            : `Mapeo actualizado a ${OFFICIAL_COST_CATEGORY_LABELS[category]} para esta y futuras liquidaciones.`
        );
      } catch (mappingError) {
        console.error('Error updating receiver cost mapping:', mappingError);
        toast.error('No se pudo actualizar el mapeo de costos.');
      } finally {
        setSavingMappingId('');
      }
      return;
    }

    setPendingCategories((current) => ({
      ...current,
      [normalizedName]: category,
    }));
    if (!mappingOnly) {
      onCostLinesChange(
        costLines.map((costLine) =>
          normalizeReceiverCostConcept(costLine.originalName) === normalizedName
            ? {
                ...costLine,
                reviewedOfficialCategory: category,
              }
            : costLine
        )
      );
    }
  };

  const handleExporterCategoryChange = async (
    line: ResolvedSettlementCostLine,
    categoryId: number | null
  ) => {
    if (!exporterReceiverId) return;
    if ((exporterMappings[line.normalizedName] ?? null) === categoryId) return;

    setSavingExporterConcept(line.normalizedName);
    try {
      await setReceiverCostExporterCategories(exporterReceiverId, [
        { name: line.originalName, categoryId },
      ]);
      setExporterMappings((current) => {
        const next = { ...current };
        if (categoryId === null) delete next[line.normalizedName];
        else next[line.normalizedName] = categoryId;
        return next;
      });
      toast.success(
        'Categoría exportadora guardada para este concepto (aplica también a liquidaciones anteriores desde 2026-2027).'
      );
    } catch (categoryError) {
      console.error('Error saving exporter cost category:', categoryError);
      toast.error('No se pudo guardar la categoría exportadora.');
    } finally {
      setSavingExporterConcept('');
    }
  };

  const handleAmountChange = (lineId: string, value: string) => {
    setAmountDrafts((current) => ({ ...current, [lineId]: value }));
  };

  const normalizeCurrency = (value: string | null | undefined) => {
    const normalized = String(value || '')
      .trim()
      .toUpperCase();
    return normalized === 'CNY' ? 'RMB' : normalized;
  };

  const updateCostLine = (
    lineId: string,
    update: (line: ExtractedSettlementCostLine) => ExtractedSettlementCostLine
  ) => {
    let updatedLine: ExtractedSettlementCostLine | undefined;
    const nextCostLines = costLines.map((costLine) => {
      if (costLine.id !== lineId) return costLine;
      updatedLine = update(costLine);
      return updatedLine;
    });

    onCostLinesChange(nextCostLines);
    if (updatedLine) {
      onResolvedLinesChange(
        resolvedLines.map((resolvedLine) =>
          resolvedLine.id === lineId
            ? {
                ...resolvedLine,
                amount: updatedLine!.amount,
                currency: updatedLine!.currency,
                sourceLocation: updatedLine!.sourceLocation,
              }
            : resolvedLine
        )
      );
    }
  };

  const handleAmountBlur = (line: ResolvedSettlementCostLine) => {
    const draft = amountDrafts[line.id];
    if (draft === undefined) return;
    const amount = Number(draft);
    if (!draft.trim() || !Number.isFinite(amount)) {
      setAmountDrafts((current) => ({
        ...current,
        [line.id]: String(line.amount),
      }));
      return;
    }

    updateCostLine(line.id, (costLine) => {
      if (costLine.traceabilityLevel === 'manual') {
        return { ...costLine, amount };
      }
      const sourceLocation = costLine.sourceLocation || {};
      const extractedAmount =
        typeof sourceLocation.extractedAmount === 'number'
          ? sourceLocation.extractedAmount
          : costLine.amount;
      return {
        ...costLine,
        amount,
        sourceLocation: {
          ...sourceLocation,
          extractedAmount,
          amountEdited: Math.abs(amount - extractedAmount) > 0.000001,
        },
      };
    });
    setAmountDrafts((current) => ({
      ...current,
      [line.id]: String(amount),
    }));
  };

  const handleCurrencyChange = (lineId: string, currency: string) => {
    const reviewedCurrency = normalizeCurrency(currency);
    if (!reviewedCurrency) return;

    updateCostLine(lineId, (costLine) => {
      if (costLine.traceabilityLevel === 'manual') {
        return { ...costLine, currency: reviewedCurrency };
      }
      const sourceLocation = costLine.sourceLocation || {};
      const extractedCurrency =
        typeof sourceLocation.extractedCurrency === 'string'
          ? normalizeCurrency(sourceLocation.extractedCurrency)
          : normalizeCurrency(costLine.currency);
      return {
        ...costLine,
        currency: reviewedCurrency,
        sourceLocation: {
          ...sourceLocation,
          extractedCurrency,
          currencyEdited: reviewedCurrency !== extractedCurrency,
        },
      };
    });
  };

  const getCurrencyOptions = (lineCurrency: string) =>
    Array.from(
      new Set(
        [
          'USD',
          normalizeCurrency(localCurrency),
          normalizeCurrency(lineCurrency),
        ].filter(Boolean)
      )
    );

  const handleAddManualLine = () => {
    const category = categoryFilter === 'all' ? 'other_costs' : categoryFilter;
    const manualLineNumber =
      costLines.filter((line) => line.traceabilityLevel === 'manual').length +
      1;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `manual-cost:${crypto.randomUUID()}`
        : `manual-cost:${Date.now()}-${Math.random().toString(16).slice(2)}`;

    onCostLinesChange([
      ...costLines,
      {
        id,
        originalName: `Costo manual ${manualLineNumber}`,
        amount: 0,
        currency:
          category === 'freight'
            ? 'USD'
            : normalizeCurrency(localCurrency) || 'USD',
        suggestedOfficialCategory: category,
        sourceLocation: { manuallyAdded: true },
        traceabilityLevel: 'manual',
      },
    ]);
  };

  const handleReviewedNameBlur = (line: ResolvedSettlementCostLine) => {
    const draft = nameDrafts[line.id];
    if (draft === undefined) return;
    const originalName = draft.trim();
    if (!originalName) {
      setNameDrafts((current) => ({
        ...current,
        [line.id]: line.originalName,
      }));
      return;
    }

    const previousNormalizedName = normalizeReceiverCostConcept(
      line.originalName
    );
    const nextNormalizedName = normalizeReceiverCostConcept(originalName);
    setPendingCategories((current) => {
      const next = { ...current };
      if (previousNormalizedName !== nextNormalizedName) {
        delete next[previousNormalizedName];
      }
      next[nextNormalizedName] = line.officialCategory;
      return next;
    });

    updateCostLine(line.id, (costLine) => {
      if (costLine.traceabilityLevel === 'manual') {
        return {
          ...costLine,
          originalName,
          suggestedOfficialCategory: line.officialCategory,
          reviewedOfficialCategory: line.officialCategory,
          receiverCostMappingId: undefined,
        };
      }

      const sourceLocation = costLine.sourceLocation || {};
      const extractedOriginalName =
        typeof sourceLocation.extractedOriginalName === 'string'
          ? sourceLocation.extractedOriginalName
          : costLine.originalName;
      return {
        ...costLine,
        originalName,
        reviewedOfficialCategory: line.officialCategory,
        receiverCostMappingId: undefined,
        sourceLocation: {
          ...sourceLocation,
          extractedOriginalName,
          originalNameEdited: originalName !== extractedOriginalName,
        },
      };
    });
  };

  const handleDeleteLine = (lineId: string) => {
    onCostLinesChange(costLines.filter((line) => line.id !== lineId));
  };

  const containsAggregateLines = resolvedLines.some(
    (line) => line.traceabilityLevel === 'aggregate'
  );
  const containsNegativeLines = resolvedLines.some((line) => line.amount < 0);
  const containsSharedLines = resolvedLines.some((line) => line.shared);
  const displayedLines = resolvedLines.filter(
    (line) =>
      (categoryFilter === 'all' || line.officialCategory === categoryFilter) &&
      (exporterCategoryFilter === undefined ||
        (exporterMappings[line.normalizedName] ?? null) ===
          exporterCategoryFilter)
  );

  return (
    <div className='flex flex-col gap-3 rounded-medium border border-default-200 p-4'>
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div>
          <h3 className='text-lg font-semibold'>Costos originales cliente</h3>
          <p className='text-sm text-default-500'>
            {mappingOnly
              ? 'Estos son los costos originales guardados. Puedes corregir su categoría para futuras liquidaciones; esta liquidación conserva su clasificación histórica.'
              : 'Los mapeos guardados se aplican automáticamente. Puedes corregir conceptos, montos, moneda y categoría antes de guardar.'}
          </p>
          {!mappingOnly && (
            <p className='text-xs text-default-400'>
              El flete se calcula en USD. Las demás categorías se calculan en{' '}
              {normalizeCurrency(localCurrency) || 'la moneda local'} usando
              el tipo de cambio cuando corresponda.
            </p>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {loading && <Spinner size='sm' label='Cargando mapeos...' />}
          {!mappingOnly && (
            <Button
              size='sm'
              variant='flat'
              onPress={handleAddManualLine}
            >
              Agregar costo manual
            </Button>
          )}
        </div>
      </div>

      {!exporterReceiverId && (
        <p className='rounded-md bg-warning-50 p-3 text-sm text-warning-700'>
          Selecciona el cliente para recuperar sus mapeos de costos.
        </p>
      )}
      {error && (
        <p className='rounded-md bg-danger-50 p-3 text-sm text-danger-700'>
          {error}
        </p>
      )}
      {containsAggregateLines && (
        <p className='rounded-md bg-warning-50 p-3 text-sm text-warning-700'>
          Este robot todavía entrega totales agregados. La liquidación puede
          guardarse, pero estos registros no representan los nombres
          originales del documento.
        </p>
      )}
      {containsSharedLines && (
        <p className='rounded-md bg-primary-50 p-3 text-sm text-primary-700'>
          Estos costos vienen de un documento multi-embarque compartido con
          otras liquidaciones y no se editan aquí. Para corregirlos, elimina
          las liquidaciones del documento y vuelve a subirlo.
        </p>
      )}
      {containsNegativeLines && (
        <p className='rounded-md bg-danger-50 p-3 text-sm text-danger-700'>
          Hay costos negativos. V1 solo admite costos como deducciones
          positivas; corrige el robot antes de crear la liquidación.
        </p>
      )}

      <div className='overflow-x-auto rounded-md border border-default-200'>
        <table className='w-full min-w-[1060px] text-sm'>
          <thead className='bg-default-100 text-xs uppercase text-default-500'>
            <tr>
              <th className='px-3 py-2 text-left'>Concepto original</th>
              <th className='px-3 py-2 text-right'>Monto</th>
              <th className='px-3 py-2 text-left'>Moneda origen</th>
              <th className='px-3 py-2 text-left'>Sugerencia robot</th>
              <th className='px-3 py-2 text-left'>Categoría oficial</th>
              {showExporterCategories && (
                <th className='px-3 py-2 text-left'>Categoría exportadora</th>
              )}
              <th className='px-3 py-2 text-left'>Estado</th>
              {!mappingOnly && (
                <th className='px-3 py-2 text-right'>Acciones</th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayedLines.map((line) => (
              <tr key={line.id} className='border-t border-default-100'>
                <td className='min-w-56 px-3 py-2'>
                  {mappingOnly ? (
                    <span>{line.originalName}</span>
                  ) : (
                    <Input
                      size='sm'
                      aria-label={`Nombre para ${line.originalName}`}
                      value={nameDrafts[line.id] ?? line.originalName}
                      onValueChange={(value) =>
                        setNameDrafts((current) => ({
                          ...current,
                          [line.id]: value,
                        }))
                      }
                      onBlur={() => handleReviewedNameBlur(line)}
                    />
                  )}
                  {line.sourceLocation?.originalNameEdited === true && (
                    <span className='mt-1 block text-xs text-warning-600'>
                      Concepto corregido manualmente
                    </span>
                  )}
                </td>
                <td className='w-48 px-3 py-2'>
                  {mappingOnly ? (
                    <span className='block text-right'>
                      {line.amount.toLocaleString()}
                    </span>
                  ) : (
                    <Input
                      type='number'
                      size='sm'
                      min='0'
                      step='any'
                      aria-label={`Monto para ${line.originalName}`}
                      value={amountDrafts[line.id] ?? String(line.amount)}
                      onValueChange={(value) =>
                        handleAmountChange(line.id, value)
                      }
                      onBlur={() => handleAmountBlur(line)}
                    />
                  )}
                  {line.sourceLocation?.amountEdited === true && (
                    <span className='mt-1 block text-xs text-warning-600'>
                      Monto corregido manualmente
                    </span>
                  )}
                </td>
                <td className='w-40 px-3 py-2'>
                  {mappingOnly ? (
                    <span>{normalizeCurrency(line.currency)}</span>
                  ) : (
                    <Select
                      size='sm'
                      aria-label={`Moneda de origen para ${line.originalName}`}
                      selectedKeys={[normalizeCurrency(line.currency)]}
                      onSelectionChange={(keys) => {
                        const currency = Array.from(keys)[0];
                        if (typeof currency !== 'string') return;
                        handleCurrencyChange(line.id, currency);
                      }}
                    >
                      {getCurrencyOptions(line.currency).map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                  {line.sourceLocation?.currencyEdited === true && (
                    <span className='mt-1 block text-xs text-warning-600'>
                      Moneda corregida manualmente
                    </span>
                  )}
                </td>
                <td className='px-3 py-2'>
                  {
                    OFFICIAL_COST_CATEGORY_LABELS[
                      line.suggestedOfficialCategory
                    ]
                  }
                </td>
                <td className='w-56 px-3 py-2'>
                  <Select
                    size='sm'
                    aria-label={`Categoría oficial para ${line.originalName}`}
                    selectedKeys={[line.officialCategory]}
                    isDisabled={loading || savingMappingId === line.mappingId}
                    onSelectionChange={(keys) => {
                      const category = Array.from(keys)[0];
                      if (typeof category !== 'string') return;
                      handleCategoryChange(
                        line,
                        category as OfficialCostCategory
                      );
                    }}
                  >
                    {OFFICIAL_COST_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {OFFICIAL_COST_CATEGORY_LABELS[category]}
                      </SelectItem>
                    ))}
                  </Select>
                </td>
                {showExporterCategories && (
                  <td className='w-56 px-3 py-2'>
                    <Select
                      size='sm'
                      aria-label={`Categoría exportadora para ${line.originalName}`}
                      selectedKeys={[
                        String(
                          exporterMappings[line.normalizedName] ??
                            'unclassified'
                        ),
                      ]}
                      isDisabled={
                        savingExporterConcept === line.normalizedName
                      }
                      onSelectionChange={(keys) => {
                        const key = Array.from(keys)[0];
                        if (typeof key !== 'string') return;
                        handleExporterCategoryChange(
                          line,
                          key === 'unclassified' ? null : Number(key)
                        );
                      }}
                    >
                      {[
                        <SelectItem key='unclassified' value='unclassified'>
                          {getDefaultExporterCostCategory(
                            exporterCategories,
                            line.officialCategory
                          )
                            ? `Automática: ${
                                getDefaultExporterCostCategory(
                                  exporterCategories,
                                  line.officialCategory
                                )!.name
                              }`
                            : UNCLASSIFIED_EXPORTER_COST_LABEL}
                        </SelectItem>,
                        ...exporterCategories
                          .filter(
                            (category) =>
                              !category.archivedAt ||
                              exporterMappings[line.normalizedName] ===
                                category.id
                          )
                          .map((category) => (
                            <SelectItem
                              key={String(category.id)}
                              value={String(category.id)}
                            >
                              {category.name}
                            </SelectItem>
                          )),
                      ]}
                    </Select>
                  </td>
                )}
                <td className='px-3 py-2'>
                  <div className='flex flex-wrap gap-1'>
                    {line.traceabilityLevel === 'manual' && (
                      <Chip size='sm' color='secondary' variant='flat'>
                        Manual
                      </Chip>
                    )}
                    {line.shared && (
                      <Chip size='sm' variant='flat'>
                        Compartido
                      </Chip>
                    )}
                    <Chip
                      size='sm'
                      color={
                        line.mappingStatus === 'saved' ? 'success' : 'warning'
                      }
                      variant='flat'
                    >
                      {line.mappingStatus === 'saved'
                        ? 'Mapeo guardado'
                        : 'Nuevo mapeo'}
                    </Chip>
                  </div>
                </td>
                {!mappingOnly && (
                  <td className='px-3 py-2 text-right'>
                    <Button
                      size='sm'
                      color='danger'
                      variant='light'
                      onPress={() => handleDeleteLine(line.id)}
                    >
                      Eliminar
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {displayedLines.length === 0 && (
              <tr>
                <td
                  colSpan={
                    (mappingOnly ? 6 : 7) + (showExporterCategories ? 1 : 0)
                  }
                  className='px-3 py-8 text-center text-default-400'
                >
                  No hay líneas en esta categoría. Puedes agregar un costo
                  manual.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReceiverCostClassificationTable;
